import { Flex, Icon, IconButton, Image } from '@chakra-ui/react'
import { Expand, Minus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useIsPartner, usePartnerPopupLogo } from '~/hooks/useCurrentPartner'
import {
  useMainWindow,
  usePopupMinimize,
  usePopupWindow,
} from '~/hooks/useElectronWindow'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { QueueMeta } from '~/pages/Popup/PopupPage'

import { POPUP_HEADER_HEIGHT } from '@mangovoice/constants'
import { PopupQueueItem } from '@mangovoice/types'

export const PopupHeader = (props: {
  meta: QueueMeta
  activeTab: 'calls' | 'messages'
}) => {
  const { t } = useTranslation()
  const { meta } = props
  const isPartner = useIsPartner()
  const PartnerLogo = usePartnerPopupLogo()
  const { focusMainWindow } = useMainWindow()
  const { hidePopupWindow } = usePopupWindow()
  const { isMinimized, toggleMinimize } = usePopupMinimize()
  const [, setMessageQueue] =
    useSharedVariable<PopupQueueItem[]>('MessageQueue')
  const [, setHybridCallState] =
    useSharedVariable<Record<string, any>>('HybridCallState')

  const handleLogoClick = () => {
    if (isPartner) {
      return
    }

    focusMainWindow()

    if (meta.calls.count === 0 && meta.messages.count === 0) {
      hidePopupWindow()
    }
  }

  const handleDismiss = () => {
    setMessageQueue([])
    setHybridCallState({})
    hidePopupWindow()
  }

  return (
    <Flex
      id="popup-header"
      justifyContent={'space-between'}
      alignItems={'center'}
      padding={4}
      zIndex={1}
      css={{
        backgroundImage:
          'var(--chakra-colors-gradients-mango-brand-popup_background)',
      }}
      borderBottomRadius="lg"
      flexShrink={0}
      height={`${POPUP_HEADER_HEIGHT}px`}
    >
      <Flex
        onClick={handleLogoClick}
        height="28px"
        cursor={isPartner ? 'default' : 'pointer'}
      >
        <Image
          src={PartnerLogo}
          alt={t('popup.header.logoAlt')}
          width="100%"
          height="100%"
          objectFit="contain"
        />
      </Flex>
      <Flex gap={2}>
        <IconButton
          aria-label={
            isMinimized ? t('popup.header.expand') : t('popup.header.minimize')
          }
          onClick={() => toggleMinimize()}
          borderRadius="full"
          size={'sm'}
          bg="bg.panel"
          color="fg.default"
        >
          <Icon as={isMinimized ? Expand : Minus} />
        </IconButton>
        <IconButton
          aria-label={t('popup.header.dismiss')}
          onClick={() => handleDismiss()}
          borderRadius="full"
          size={'sm'}
          bg="bg.panel"
          color="fg.default"
        >
          <Icon as={X} />
        </IconButton>
      </Flex>
    </Flex>
  )
}
